import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../../services/api';
import { useSSE } from '../../hooks/useSSE';

interface TicketInfo {
  id: string;
  queueConfigId: string;
  ticketNumber: number;
  status: string;
  partySize: number;
  customerName: string | null;
  issuedAt: string;
  aheadCount: number;
  estimatedWaitMinutes: number;
  storeName: string;
  currentServing: number;
}

const MyTicketPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [ticket, setTicket] = useState<TicketInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const fetchTicket = useCallback(async () => {
    if (!token) return;
    try {
      const response = await publicApi.getTicket(token);
      setTicket(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || '找不到號碼牌');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 初次載入
  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  // SSE 即時更新（取得 storeId 後連線）
  useSSE({
    storeId: ticket?.queueConfigId || '',
    enabled: !!ticket?.queueConfigId,
    onEvent: () => { fetchTicket(); },
  });

  // 降級備援：30 秒輪詢
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(fetchTicket, 30000);
    return () => clearInterval(interval);
  }, [token, fetchTicket]);

  const handleCancel = async () => {
    if (!token || cancelling) return;
    if (!window.confirm('確定要取消排隊嗎？')) return;

    setCancelling(true);
    try {
      await publicApi.cancelTicket(token);
      // 重新載入
      const response = await publicApi.getTicket(token);
      setTicket(response.data);
    } catch (err: any) {
      alert(err.response?.data?.message || '取消失敗');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-xl text-gray-600 dark:text-gray-400">{error || '找不到號碼牌'}</p>
        </div>
      </div>
    );
  }

  const isCalled = ticket.status === 'CALLED';
  const isServing = ticket.status === 'SERVING';
  const isWaiting = ['WAITING', 'NOTIFIED'].includes(ticket.status);
  const isNotified = ticket.status === 'NOTIFIED';
  const isDone = ['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(ticket.status);

  const getStatusColor = () => {
    if (isCalled) return 'bg-green-500';
    if (isServing) return 'bg-blue-500';
    if (isNotified) return 'bg-yellow-500';
    if (isDone) return 'bg-slate-400';
    return 'bg-brand-600';
  };

  const getStatusText = () => {
    if (isCalled) return '輪到你了！';
    if (isServing) return '服務中';
    if (isNotified) return '快到你了！';
    if (ticket.status === 'COMPLETED') return '已完成';
    if (ticket.status === 'CANCELLED') return '已取消';
    if (ticket.status === 'NO_SHOW') return '未到場';
    return '等候中';
  };

  return (
    <div className={`min-h-screen ${isCalled ? 'bg-green-500' : 'bg-white dark:bg-slate-950'} transition-colors duration-500`}>
      <div className="max-w-md mx-auto p-4 pt-8">
        {/* 店名 */}
        <p className={`text-center text-sm mb-2 ${isCalled ? 'text-green-100' : 'text-gray-500 dark:text-gray-400'}`}>
          {ticket.storeName}
        </p>

        {/* 我的號碼 */}
        <div className={`${isCalled ? 'bg-white' : 'bg-white dark:bg-slate-800 shadow'} rounded-2xl p-8 text-center mb-6`}>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">我的號碼</p>
          <p className="text-8xl font-bold text-gray-900 dark:text-white mb-4">#{ticket.ticketNumber}</p>

          {/* 狀態 */}
          <div className={`inline-block ${getStatusColor()} text-white px-6 py-2 rounded-full text-lg font-bold mb-4`}>
            {getStatusText()}
          </div>

          {/* 等候資訊 */}
          {isWaiting && (
            <div className="mt-4">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                前面還有 <span className="text-orange-500">{ticket.aheadCount}</span> 組
              </p>
              <p className="text-gray-500 dark:text-gray-400 mt-1">
                預估等待 ~{ticket.estimatedWaitMinutes} 分鐘
              </p>
            </div>
          )}

          {/* 輪到你了 - 震動提示 */}
          {isCalled && (
            <div className="mt-4">
              <p className="text-xl font-bold text-green-600">請到櫃台報到</p>
              <p className="text-gray-500 mt-1 text-sm">目前服務到 #{ticket.currentServing}</p>
            </div>
          )}

          {/* 已完成/取消 */}
          {isDone && (
            <div className="mt-4">
              <p className="text-gray-500 dark:text-gray-400">
                {ticket.status === 'COMPLETED' ? '感謝您的等候' :
                 ticket.status === 'CANCELLED' ? '排隊已取消' :
                 '因未到場已取消'}
              </p>
            </div>
          )}
        </div>

        {/* 號碼牌資訊 */}
        <div className={`${isCalled ? 'bg-green-600 bg-opacity-20' : 'bg-white dark:bg-slate-800 shadow'} rounded-lg p-4 mb-6`}>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className={`text-sm ${isCalled ? 'text-green-100' : 'text-gray-500 dark:text-gray-400'}`}>人數</p>
              <p className={`text-lg font-bold ${isCalled ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{ticket.partySize} 人</p>
            </div>
            <div>
              <p className={`text-sm ${isCalled ? 'text-green-100' : 'text-gray-500 dark:text-gray-400'}`}>目前服務號</p>
              <p className={`text-lg font-bold ${isCalled ? 'text-white' : 'text-gray-900 dark:text-white'}`}>#{ticket.currentServing || 0}</p>
            </div>
          </div>
        </div>

        {/* 取消按鈕 */}
        {isWaiting && (
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="w-full bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 font-bold py-3 rounded-lg text-sm transition-all duration-200 disabled:opacity-50"
          >
            {cancelling ? '取消中...' : '取消排隊'}
          </button>
        )}

        {/* 自動刷新提示 */}
        {!isDone && (
          <p className={`text-center text-xs mt-4 ${isCalled ? 'text-green-200' : 'text-gray-400 dark:text-gray-500'}`}>
            即時更新中
          </p>
        )}
      </div>
    </div>
  );
};

export default MyTicketPage;
